import PropTypes from 'prop-types';
import { BsInfoCircle, BsTrash } from 'react-icons/bs';

import Card from '#globalComponents/Card/Card';
import Table from '#globalComponents/Table/Table';
import { ROW_ACTION_BUTTON } from '#globalComponents/Table/tableStyles';

const badgeClasses =
	'inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent';

const VariableHeader = ({ label, variable, variableClassName = 'bg-primary-100 text-primary-700' }) => (
	<div className="flex flex-col gap-1">
		<span>{label}</span>
		{variable && <span className={`${badgeClasses} ${variableClassName}`}>{variable}</span>}
	</div>
);

VariableHeader.propTypes = {
	label: PropTypes.string.isRequired,
	variable: PropTypes.string,
	variableClassName: PropTypes.string,
};

const TEXT_COLUMNS = [
	{ key: 'firstName', label: 'Vorname', variable: '{firstName}' },
	{ key: 'lastName', label: 'Nachname', variable: '{lastName}' },
	{ key: 'salutation', label: 'Anrede' },
	{ key: 'fullname', label: 'Vollständiger Name', variable: '{fullName}' },
	{ key: 'emailAddress', label: 'E-Mail', variable: '{email}', variableClassName: 'bg-primary-200 text-primary-700' },
	{ key: 'companyName', label: 'Firma', variable: '{company}' },
].map(({ key, label, variable, variableClassName }) => ({
	key,
	label: <VariableHeader label={label} variable={variable} variableClassName={variableClassName} />,
	render: (lead) => <span>{lead[key] || '-'}</span>,
}));

const RecipientVariablesTable = ({ leads = [], onRemoveRecipient, removingRecipientId }) => {
	if (leads.length === 0) {
		return (
			<Card>
				<div className="text-center py-8">
					<p className="text-sm text-bgBase-800">Keine Kontakte vorhanden</p>
				</div>
			</Card>
		);
	}

	const columns = [
		...TEXT_COLUMNS,
		...(onRemoveRecipient
			? [
					{
						key: 'remove',
						label: '',
						width: '3rem',
						render: (lead) =>
							// Company-only recipients (a company with no contact person) have no contact
							// id; they cannot be removed per-campaign (the remove endpoint acts on a
							// crmContactId only — manage them on the contact list).
							lead.id ? (
								<button
									onClick={() => onRemoveRecipient(lead)}
									disabled={removingRecipientId === lead.id}
									className={`${ROW_ACTION_BUTTON} disabled:opacity-50 disabled:cursor-not-allowed`}
									title="Empfänger entfernen"
								>
									{removingRecipientId === lead.id ? (
										<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500" />
									) : (
										<BsTrash />
									)}
								</button>
							) : null,
					},
				]
			: []),
	];

	return (
		<Card>
			<div className="flex items-center justify-between mb-4">
				<h3 className="text-lg font-semibold">Kontakt-Übersicht</h3>
				<span className="text-sm text-bgBase-800">
					{leads.length} {leads.length === 1 ? 'Kontakt' : 'Kontakte'}
				</span>
			</div>

			{/* Info Banner */}
			<div className="flex items-start gap-3 p-4 rounded-card bg-primary-100 border border-primary-200 mb-4">
				<BsInfoCircle className="h-5 w-5 text-primary-700 flex-shrink-0 mt-0.5" />
				<div className="text-sm text-primary-700">
					<p className="font-medium mb-1">Verfügbare Variablen</p>
					<p>Diese Werte werden beim Versand automatisch durch die entsprechenden Kontaktdaten ersetzt.</p>
				</div>
			</div>

			<Table
				rows={leads}
				columns={columns}
				rowId={(lead) => lead.id || `company:${lead.crmCompanyId}`}
				emptyLabel="Keine Kontakte vorhanden"
			/>
		</Card>
	);
};

RecipientVariablesTable.propTypes = {
	leads: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string,
			crmCompanyId: PropTypes.string,
			fullname: PropTypes.string,
			firstName: PropTypes.string,
			lastName: PropTypes.string,
			salutation: PropTypes.string,
			emailAddress: PropTypes.string,
			companyName: PropTypes.string,
		}),
	),
	onRemoveRecipient: PropTypes.func,
	removingRecipientId: PropTypes.string,
};

export default RecipientVariablesTable;
